import { motion, AnimatePresence } from 'motion/react';
import { useIntlayer } from 'react-intlayer';

import { LocalizedLink } from '~/components/LocalizedLink';
import { SOCIAL_LINKS } from '~/constants';

/* ── Motion tokens ──────────────────────────────── */
const OVERLAY_DURATION = 1.2;
const STAGGER_DELAY = 0.06;
const EASE = [0.16, 1, 0.3, 1] as const;

/** Fade-in from below for each menu item. */
const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, delay: i * STAGGER_DELAY, ease: EASE },

  }),
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

const NAV_ITEMS = [
  { to: '/', key: 'home' },
  { to: '/products/chorna', key: 'chorna' },
  { to: '/products/tactical-drones', key: 'tacticalDrones' },
  { to: '/products/iris', key: 'jade' },
  { to: '/about', key: 'about' },
] as const;

interface MenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Menu = ({ isOpen, onClose }: MenuProps) => {
  const t = useIntlayer('mobile-menu');

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-40 bg-black md:hidden origin-top"
          initial={{ clipPath: 'inset(0 0 100% 0)' }}
          animate={{ clipPath: 'inset(0 0 0% 0)' }}
          exit={{ clipPath: 'inset(0 0 100% 0)' }}
          transition={{ duration: OVERLAY_DURATION, ease: EASE }}
        >
          <nav className="flex flex-col gap-10 px-4 py-10 pt-[90px]">
            {/* Navigation section */}
            <div className="flex flex-col gap-2.5">
              <p
                className="uppercase text-sm tracking-[0.01em]"
                style={{
                  fontFamily: 'var(--font-fragment-mono)',
                  color: '#82919F',
                }}
              >
                {t.navigation}
              </p>
              <div className="flex flex-col gap-2">
                {NAV_ITEMS.map((item, i) => (
                  <motion.div
                    key={item.key}
                    custom={i}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <LocalizedLink
                      to={item.to}
                      className="block py-2 text-xl text-white font-normal leading-[1.4]"
                      style={{ fontFamily: 'var(--font-manrope)' }}
                      onClick={onClose}
                    >
                      {t[item.key]}
                    </LocalizedLink>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Quick Contact section */}
            <div className="flex flex-col gap-2.5">
              <p
                className="uppercase text-sm tracking-[0.01em]"
                style={{
                  fontFamily: 'var(--font-fragment-mono)',
                  color: '#82919F',
                }}
              >
                {t.quickContact}
              </p>
              <motion.div
                custom={NAV_ITEMS.length}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <a
                  href={SOCIAL_LINKS.LINKEDIN}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block py-2 text-xl text-white font-normal leading-[1.4]"
                  style={{ fontFamily: 'var(--font-manrope)' }}
                  onClick={onClose}
                >
                  {t.linkedin}
                </a>
              </motion.div>
            </div>
          </nav>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
